import Sidebar from '@/components/Sidebar/Sidebar';
import { useAuthStore } from '@/store/authStore';
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { FC, ReactNode, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

interface LayoutProps {
  children: ReactNode;
}

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: var(--gray-50);

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TopBar = styled.header`
  background: white;
  border-bottom: 1px solid var(--gray-200);
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (min-width: 769px) {
    .mobile-menu-button {
      display: none;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--gray-600);
  cursor: pointer;
  padding: 0.5rem;

  @media (max-width: 768px) {
    display: block;
  }

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  max-width: 1560px;
  margin: 0 auto;
  width: 100%;
`;

const Overlay = styled.div<{ show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 40;
  display: ${(props) => (props.show ? 'block' : 'none')};

  @media (min-width: 769px) {
    display: none;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const HeaderUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const UserAvatar = styled.div`
  width: 2rem;
  height: 2rem;
  background-color: var(--primary-100);
  color: var(--primary-700);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;
`;

const UserName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-900);
`;

const UserEmail = styled.span`
  font-size: 0.75rem;
  color: var(--gray-600);
`;

const HeaderLogoutButton = styled.button`
  background: none;
  border: 1px solid var(--gray-300);
  color: var(--gray-600);
  padding: 0.5rem;
  border-radius: 0.375rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  transition: all 0.2s;

  &:hover {
    border-color: var(--gray-400);
    color: var(--gray-700);
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const Layout: FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clearAuth, isAuthenticated } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    clearAuth();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Check if we're on a public route
  const isPublicRoute = ['/', '/login', '/register'].includes(
    location.pathname
  );

  if (isPublicRoute) {
    return <>{children}</>;
  }

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <LayoutContainer>
      <Overlay show={sidebarOpen} onClick={closeSidebar} />

      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <MainContent>
        <TopBar>
          <MobileMenuButton
            className="mobile-menu-button"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <XMarkIcon /> : <Bars3Icon />}
          </MobileMenuButton>

          <div></div>

          <HeaderActions>
            {user ? (
              <HeaderUserInfo>
                <UserAvatar>{user.full_name?.charAt(0) || 'U'}</UserAvatar>
                <UserDetails>
                  <UserName>{user.full_name}</UserName>
                  <UserEmail>{user.email}</UserEmail>
                </UserDetails>
              </HeaderUserInfo>
            ) : (
              <UserEmail>Loading user...</UserEmail>
            )}

            <HeaderLogoutButton onClick={handleLogout} title="Sign out">
              <ArrowRightOnRectangleIcon />
            </HeaderLogoutButton>
          </HeaderActions>
        </TopBar>

        <ContentArea>{children}</ContentArea>
      </MainContent>
    </LayoutContainer>
  );
};

export default Layout;
